import {HubCollectionSummary} from '@/components/admin/HubCollectionSummary';
import { useEffect, useState, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";

type Line = { key: string; label: string; amountPence: number; source: string };
type StoreRow = import("@workspace/api-client-react").HubStoreWeeklyFees & {
  storeId: number; storeName: string;
  platformFeesPence: number; hubDriverFeesPence: number;
  ledgerFeesPence: number; tipSharePence: number;
  totalPence: number; orders: number;
};
type Report = {
  from: string; to: string;
  retained: Line[]; totalRetainedPence: number;
  passedThrough: Array<Omit<Line,"amountPence"> & {amountPence:number|null}>; totalPassedThroughPence: number|null;
  stripeFeeEvidence: "verified"|"unavailable";
  byStore: StoreRow[];
};

const pence = (p: number|null) => p===null?"Unavailable":`£${(p / 100).toFixed(2)}`;
const isoDaysAgo = (d: number) => new Date(Date.now() - d * 86400_000).toISOString().slice(0, 10);

const RANGES = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "This year", days: 365 },
];

export default function AdminHubRevenue() {
  const { toast } = useToast();
  const [days, setDays] = useState(30);
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("ayrshire_token");
      const qs = new URLSearchParams({
        from: new Date(`${from}T00:00:00`).toISOString(),
        to: new Date(`${to}T23:59:59`).toISOString(),
      });
      const r = await fetch(`/api/admin/hub-reconciliation?${qs}`, {
        headers: { Authorization: `Bearer ${token ?? ""}` },
      });
      if (!r.ok) throw new Error("failed");
      setData(await r.json() as Report);
    } catch {
      toast({ title: "Could not load the report", variant: "destructive" });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, toast]);

  useEffect(() => { void load(); }, [load]);

  function pickRange(d: number) {
    setDays(d);
    setFrom(isoDaysAgo(d));
    setTo(new Date().toISOString().slice(0, 10));
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">Hub finance</h1>
          <p className="text-sm text-slate-500 mt-1">
            Recorded fee and income entries. Some amounts are still owed by shops. Payment movements may include money allocated to shops, drivers and kindness. These operational figures do not determine legal supplier status, VAT turnover or cash profit. If Hub supplies delivery, the gross delivery charge may be its own consideration even when the driver receives the same amount.
          </p>
        </div>

        {/* Range */}
        <div className="flex flex-wrap items-center gap-2">
          {RANGES.map(r => (
            <button
              key={r.days}
              onClick={() => pickRange(r.days)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                days === r.days ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {r.label}
            </button>
          ))}
          <div className="flex items-center gap-2 ml-auto text-sm">
            <input type="date" value={from} max={to} onChange={e => { setFrom(e.target.value); setDays(0); }}
              className="border border-slate-200 rounded-lg px-2 py-1.5" aria-label="From date" />
            <span className="text-slate-400">to</span>
            <input type="date" value={to} min={from} onChange={e => { setTo(e.target.value); setDays(0); }}
              className="border border-slate-200 rounded-lg px-2 py-1.5" aria-label="To date" />
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400 text-sm">Loading…</div>
        ) : !data ? (
          <div className="py-16 text-center text-slate-400 text-sm">No data for this period.</div>
        ) : (
          <>
            {data.stripeFeeEvidence==='unavailable' && <p role="status" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">Stripe fee evidence is unavailable. Costs are incomplete and must not be treated as zero.</p>}
            <HubCollectionSummary from={from} to={to} recordedRevenuePence={data.totalRetainedPence}/>
            {/* Headline */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
                <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Recorded revenue before costs</div>
                <div className="text-3xl font-bold text-emerald-800 mt-1">{pence(data.totalRetainedPence)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Driver allocations and Stripe costs</div>
                <div className="text-3xl font-bold text-slate-700 mt-1">{pence(data.totalPassedThroughPence)}</div>
              </div>
            </div>

            {/* Retained */}
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 font-semibold text-slate-800">Recorded fees and earnings</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[520px]">
                  <tbody>
                    {data.retained.filter(l => l.amountPence !== 0).map(l => (
                      <tr key={l.key} className="border-b border-slate-50 last:border-0">
                        <td className="px-5 py-3">
                          <div className="font-medium text-slate-800">{l.label}</div>
                          {/* Every figure names its origin so a disputed line can be traced. */}
                          <div className="text-xs text-slate-400 mt-0.5">{l.source}</div>
                        </td>
                        <td className="px-5 py-3 text-right font-semibold text-emerald-700 whitespace-nowrap">
                          {pence(l.amountPence)}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-slate-50">
                      <td className="px-5 py-3 font-bold text-slate-800">Recorded revenue before costs</td>
                      <td className="px-5 py-3 text-right font-bold text-emerald-800 whitespace-nowrap">
                        {pence(data.totalRetainedPence)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Passed through */}
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100">
                <span className="font-semibold text-slate-800">Allocations and processing costs</span>
                <span className="text-xs text-slate-400 ml-2">operational allocations and expenses; bank arrival requires statement evidence</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[520px]">
                  <tbody>
                    {data.passedThrough.map(l => (
                      <tr key={l.key} className="border-b border-slate-50 last:border-0">
                        <td className="px-5 py-3">
                          <div className="font-medium text-slate-800">{l.label}</div>
                          <div className="text-xs text-slate-400 mt-0.5">{l.source}</div>
                        </td>
                        <td className="px-5 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">
                          {pence(l.amountPence)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Per store */}
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100">
                <span className="font-semibold text-slate-800">Recorded fees by shop</span>
                {/* Weekly shop account charges are shown separately from customer-paid
                    order fees. Hub Dash remains a separate customer product. */}
                <span className="text-xs text-slate-400 ml-2">order fees and shop account charges</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[680px]">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-5 py-2 text-left font-semibold">Shop</th>
                      <th className="px-3 py-2 text-right font-semibold">Orders</th>
                      <th className="px-3 py-2 text-right font-semibold">Platform</th>
                      <th className="px-3 py-2 text-right font-semibold">Platform driver</th>
                      <th className="px-3 py-2 text-right font-semibold">Other ledger</th>
                      <th className="px-3 py-2 text-right font-semibold">Weekly fees</th>
                      <th className="px-3 py-2 text-right font-semibold">Tips</th>
                      <th className="px-5 py-2 text-right font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byStore.map(s => (
                      <tr key={s.storeId} className="border-t border-slate-50">
                        <td className="px-5 py-3 font-medium text-slate-800">{s.storeName}</td>
                        <td className="px-3 py-3 text-right text-slate-500">{s.orders}</td>
                        <td className="px-3 py-3 text-right text-slate-600 whitespace-nowrap">{pence(s.platformFeesPence)}</td>
                        <td className="px-3 py-3 text-right text-slate-600 whitespace-nowrap">{pence(s.hubDriverFeesPence)}</td>
                        <td className="px-3 py-3 text-right text-slate-600 whitespace-nowrap">{pence(s.ledgerFeesPence-(s.weeklyShopFeesPence??0))}</td>
                        <td className="px-3 py-3 text-right text-slate-600 whitespace-nowrap">{pence(s.weeklyShopFeesPence??0)}</td>
                        <td className="px-3 py-3 text-right text-slate-600 whitespace-nowrap">{pence(s.tipSharePence)}</td>
                        <td className="px-5 py-3 text-right font-semibold text-emerald-700 whitespace-nowrap">{pence(s.totalPence)}</td>
                      </tr>
                    ))}
                    {data.byStore.length === 0 && (
                      <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-400">No shop activity in this period.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
